import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const HorizontalGallery = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [hoveredId, setHoveredId] = useState(null);
  const sectionRef = useRef(null);
  const scrollContainerRef = useRef(null);

  // Inject responsive styles
  useEffect(() => {
    const styleId = 'horizontal-gallery-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .horizontal-gallery-section {
          height: 200vh;
          position: relative;
          background-color: #0A0A0A;
        }
        
        .horizontal-gallery-card {
          transition: width 0.4s ease;
        }
        
        @media (max-width: 768px) {
          .horizontal-gallery-card {
            width: calc(100vw - 20px) !important;
          }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Fetch products from Supabase
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const { data, error } = await supabase
          .from('products')
          .select('*')
          .eq('category', 'prints')
          .order('created_at', { ascending: false })
          .range(0, 5);

        if (error) throw error;
        setProducts(data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Handle scroll for horizontal movement
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;

      const section = sectionRef.current;
      const rect = section.getBoundingClientRect();
      const sectionTop = rect.top;
      const sectionHeight = section.offsetHeight;
      const windowHeight = window.innerHeight;

      const scrollableDistance = sectionHeight - windowHeight;
      const scrolledAmount = -sectionTop;
      const progress = Math.max(0, Math.min(1, scrolledAmount / scrollableDistance));

      setScrollProgress(progress);
    };

    // Native scroll listener as always-on fallback
    window.addEventListener('scroll', handleScroll, { passive: true });

    // Subscribe to Lenis after one frame — resolves parent/child effect timing
    const rafId = requestAnimationFrame(() => {
      if (window.lenis) window.lenis.on('scroll', handleScroll);
    });

    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(rafId);
      if (window.lenis) window.lenis.off('scroll', handleScroll);
    };
  }, []);

  // Calculate horizontal scroll position based on vertical scroll progress
  useEffect(() => {
    if (!scrollContainerRef.current || products.length === 0) return;
    
    const container = scrollContainerRef.current;
    const maxScroll = container.scrollWidth - container.clientWidth;
    
    // Start horizontal scroll after expansion (after 10% progress)
    const horizontalProgress = Math.max(0, (scrollProgress - 0.1) / 0.9);
    container.scrollLeft = horizontalProgress * maxScroll;
  }, [scrollProgress, products]);

  // Determine if we've scrolled past the trigger point (10% of scroll)
  const isExpanded = scrollProgress > 0.1;
  
  // Container values based on expanded state
  const containerHeight = isExpanded ? '100vh' : '65vh';
  const containerTop = isExpanded ? '0vh' : '25vh';
  
  // Card sizing
  const cardWidth = isExpanded ? 'calc(50vw - 15px)' : 'auto';

  if (loading) {
    return (
      <section style={{
        height: '50vh',
        backgroundColor: '#0A0A0A',
      }} />
    );
  }

  return (
    <section 
      ref={sectionRef}
      className="horizontal-gallery-section"
      style={{
        height: '200vh',
        position: 'relative',
        backgroundColor: '#0A0A0A',
      }}
    >
      {/* Sticky Container */}
      <div 
        style={{
          position: 'sticky',
          top: containerTop,
          height: containerHeight,
          width: '100%',
          overflow: 'hidden',
          transition: 'height 0.4s ease, top 0.4s ease',
        }}
      >
        <div
          ref={scrollContainerRef}
          style={{
            display: 'flex',
            height: '100%',
            overflow: 'hidden',
            gap: '10px',
            padding: '0 10px',
          }}
        >
          {products.map((product) => (
            <div
              key={product.id}
              className="horizontal-gallery-card"
              style={{
                flexShrink: 0,
                height: '100%',
                width: cardWidth,
                display: 'flex',
                flexDirection: 'column',
                transition: 'width 0.4s ease',
              }}
            >
              {/* Image Container */}
              <Link
                to={`/product/${product.id}`}
                className="horizontal-gallery-image"
                style={{
                  height: 'calc(100% - 40px)',
                  aspectRatio: isExpanded ? 'unset' : '4 / 5',
                  position: 'relative',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  textDecoration: 'none',
                  display: 'block',
                }}
                onMouseEnter={() => setHoveredId(product.id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                <img
                  src={product.image_url}
                  alt={product.name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transition: 'transform 0.4s ease',
                    transform: hoveredId === product.id ? 'scale(1.03)' : 'scale(1)',
                  }}
                />
              </Link>
              
              {/* Product info below image */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 0',
                height: '40px',
              }}>
                <span style={{
                  fontSize: '16px',
                  fontWeight: '400',
                  color: '#F1F1F1',
                  fontFamily: "'TASA Orbiter Display', sans-serif",
                }}>
                  {product.name}
                </span>
                <span style={{
                  fontSize: '16px',
                  fontWeight: '400',
                  color: '#F1F1F1',
                  fontFamily: "'TASA Orbiter Display', sans-serif",
                }}>
                  € {product.price}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HorizontalGallery;
